import React, { useState } from 'react';

// Mock data sources based on user input
const sources = [
    { name: 'SAGE', subsidiaries: 13 },
    { name: 'IFS', subsidiaries: 2 },
    { name: 'PEOPLEWISE', subsidiaries: 3 },
    { name: 'FLEXI', subsidiaries: 2 },
    { name: 'TOPAZ', subsidiaries: 5 },
    { name: 'ACCSYS', subsidiaries: 1 },
    { name: 'SEAMLESS', subsidiaries: 8 },
];

// SAGE operations list
const sageOperations = [
    'MTN South_Africa',
    'Manco GSM',
    'Manco Fintech',
    'Bayobab Dubai',
    'Bayobab Kenya',
    'Bayobab Zambia',
    'MTN SA Fintech',
    'Bayobab Ghana',
    'Bayobab Nigeria',
    'Bayobab SA',
    'Ghana GSM + Momo',
    'Manco Dubai(Expats)',
    'Manco Dubai (Locals)',
];

// Operation currencies
const operationCurrencies = {
    'MTN South_Africa': 'ZAR',
    'Manco GSM': 'ZAR',
    'Manco Fintech': 'ZAR',
    'Bayobab Dubai': 'AED',
    'Bayobab Kenya': 'KES',
    'Bayobab Zambia': 'ZMW',
    'MTN SA Fintech': 'ZAR',
    'Bayobab Ghana': 'GHS',
    'Bayobab Nigeria': 'NGN',
    'Bayobab SA': 'ZAR',
    'Ghana GSM + Momo': 'GHS',
    'Manco Dubai(Expats)': 'AED',
    'Manco Dubai (Locals)': 'AED',
};

// Currency info: symbols and 1 USD to local rates (as of July 13, 2025)
const currencyInfo = {
    ZAR: { symbol: 'R', usdToLocal: 17.9224 },
    AED: { symbol: 'AED ', usdToLocal: 3.6725 },
    KES: { symbol: 'KSh ', usdToLocal: 129.2 },
    ZMW: { symbol: 'ZK ', usdToLocal: 27.213 },
    GHS: { symbol: 'GH₵ ', usdToLocal: 10.4 },
    NGN: { symbol: '₦ ', usdToLocal: 1530.59 },
};

// Function to generate mock data for a source
const generateMockData = (subsidiaries) => {
    const baseRecords = subsidiaries * 100; // Arbitrary: 100 records per subsidiary for demo
    return {
        lastUpdated: new Date().toLocaleString(), // Use current date/time
        recordsUpdated: Math.floor(Math.random() * 50) + 1, // Random 1-50 updated
        totalRecords: baseRecords + Math.floor(Math.random() * 200), // Base + random variance
    };
};

const DataPullDashboard = () => {
    // Initialize state with mock data for each source
    const [sourceData, setSourceData] = useState(
        sources.map((source) => ({
            ...source,
            ...generateMockData(source.subsidiaries),
            lastUpdateTime: null, // For checking recent updates
            operationsData: null, // Details per operation (for SAGE only)
            expanded: false, // To show/hide details
            noUpdates: false, // Flag for no updates
            loading: false,
        }))
    );

    const handleUpdate = (index) => {
        setSourceData((prevData) => {
            const newData = [...prevData];
            newData[index].loading = true;
            return newData;
        });

        // Simulate API pull with timeout
        setTimeout(() => {
            setSourceData((prevData) => {
                const newData = [...prevData];
                const now = new Date();
                const withinMinute = newData[index].lastUpdateTime && (now - new Date(newData[index].lastUpdateTime)) < 60000;

                // Update main stats
                const mock = withinMinute
                    ? { lastUpdated: new Date().toLocaleString(), recordsUpdated: 0, totalRecords: prevData[index].totalRecords }
                    : generateMockData(newData[index].subsidiaries);
                newData[index] = { ...newData[index], ...mock };

                // Generate operations data if SAGE
                if (newData[index].name === 'SAGE') {
                    const generateCounts = withinMinute ? () => 0 : () => Math.floor(Math.random() * 6); // 0-5 believable
                    const generateBenefit = (currency) => {
                        if (withinMinute) {
                            return { count: 0, totalLocal: 0, totalZAR: 0, totalUSD: 0 };
                        } else {
                            const count = Math.floor(Math.random() * 31); // 0-30 believable
                            const perItemUSD = Math.random() * 45 + 5; // 5-50 USD per item
                            const totalUSD = (count * perItemUSD).toFixed(2);
                            const totalLocal = (totalUSD * currencyInfo[currency].usdToLocal).toFixed(2);
                            const totalZAR = (totalUSD * currencyInfo['ZAR'].usdToLocal).toFixed(2);
                            return { count, totalLocal, totalZAR, totalUSD };
                        }
                    };

                    const operationsData = sageOperations.map((op) => {
                        const currency = operationCurrencies[op];
                        return {
                            name: op,
                            newHires: generateCounts(),
                            retirements: generateCounts(),
                            transfers: generateCounts(),
                            benefits: {
                                canteen: generateBenefit(currency),
                                lifestyle: generateBenefit(currency),
                                cellphone: generateBenefit(currency),
                                perDiem: generateBenefit(currency),
                            },
                        };
                    });

                    // Check if there are any updates
                    const hasUpdates = operationsData.some((op) =>
                        op.newHires > 0 || op.retirements > 0 || op.transfers > 0 ||
                        op.benefits.canteen.count > 0 || op.benefits.lifestyle.count > 0 ||
                        op.benefits.cellphone.count > 0 || op.benefits.perDiem.count > 0
                    );

                    newData[index].operationsData = operationsData;
                    newData[index].expanded = true; // Automatically expand to show details
                    newData[index].noUpdates = !hasUpdates;
                }

                newData[index].lastUpdateTime = now.toISOString();
                newData[index].loading = false;
                return newData;
            });
        }, 1500); // 1.5s delay to simulate network call
    };

    const handleCollapse = (index) => {
        setSourceData((prevData) => {
            const newData = [...prevData];
            newData[index].expanded = false;
            return newData;
        });
    };

    return (
        <div>
            <h2>API Pull Dashboard</h2>
            <p>
                 Pulling employee data from multiple API sources into a central database.
                Click "Update" to perform a manual pull for that source, updating the stats and showing detailed operation responses for SAGE.
            </p>
            <table className="table table-striped">
                <thead>
                <tr>
                    <th>Source</th>
                    <th>Last Updated</th>
                    <th>Records Updated</th>
                    <th>Total Records</th>
                    <th>Update</th>
                </tr>
                </thead>
                <tbody>
                {sourceData.map((source, index) => (
                    <React.Fragment key={source.name}>
                        <tr>
                            <td>{source.name} ({source.subsidiaries} subsidiaries)</td>
                            <td>{source.lastUpdated}</td>
                            <td>{source.recordsUpdated}</td>
                            <td>{source.totalRecords}</td>
                            <td>
                                <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => handleUpdate(index)}
                                    disabled={source.loading}
                                >
                                    {source.loading ? 'Updating...' : 'Update'}
                                </button>
                            </td>
                        </tr>
                        {source.expanded && source.operationsData && (
                            <tr>
                                <td colSpan="5">
                                    <div>
                                        <h4>Update Details for {source.name} Operations
                                            <button
                                                className="btn btn-secondary float-right"
                                                onClick={() => handleCollapse(index)}
                                            >
                                                Hide Details
                                            </button>
                                        </h4>
                                        {source.noUpdates && (
                                            <div className="alert alert-info" role="alert">
                                                There were no updates at this point.
                                            </div>
                                        )}
                                        <table className="table table-sm table-bordered">
                                            <thead>
                                            <tr>
                                                <th>Operation</th>
                                                <th>New Hires</th>
                                                <th>Retirements</th>
                                                <th>Transfers</th>
                                                <th>Canteen Subsidy (Count / Local / R / $)</th>
                                                <th>Lifestyle Benefits (Count / Local / R / $)</th>
                                                <th>Cellphone Allowance (Count / Local / R / $)</th>
                                                <th>Per Diem (Count / Local / R / $)</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {source.operationsData.map((op) => {
                                                const currency = operationCurrencies[op.name];
                                                const symbol = currencyInfo[currency].symbol;
                                                const benefits = op.benefits;
                                                return (
                                                    <tr key={op.name}>
                                                        <td>{op.name}</td>
                                                        <td>{op.newHires}</td>
                                                        <td>{op.retirements}</td>
                                                        <td>{op.transfers}</td>
                                                        <td>{benefits.canteen.count} / {symbol}{benefits.canteen.totalLocal} / R{benefits.canteen.totalZAR} / ${benefits.canteen.totalUSD}</td>
                                                        <td>{benefits.lifestyle.count} / {symbol}{benefits.lifestyle.totalLocal} / R{benefits.lifestyle.totalZAR} / ${benefits.lifestyle.totalUSD}</td>
                                                        <td>{benefits.cellphone.count} / {symbol}{benefits.cellphone.totalLocal} / R{benefits.cellphone.totalZAR} / ${benefits.cellphone.totalUSD}</td>
                                                        <td>{benefits.perDiem.count} / {symbol}{benefits.perDiem.totalLocal} / R{benefits.perDiem.totalZAR} / ${benefits.perDiem.totalUSD}</td>
                                                    </tr>
                                                );
                                            })}
                                            </tbody>
                                        </table>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </React.Fragment>
                ))}
                </tbody>
            </table>
        </div>
    );
};

export default DataPullDashboard;